import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoadingSpinner from './components/LoadingSpinner';
import AdminRoute from './components/AdminRoute';
import { initListeners } from './services/pushNotifications';

// Route components are code-split so a cold load only downloads the chunk for
// the page being visited (e.g. /login no longer pulls BookDetail, Settings,
// and the Capacitor mobile plugins). Each becomes its own lazy-loaded chunk.
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const BookList = lazy(() => import('./pages/BookList'));
const BookDetail = lazy(() => import('./pages/BookDetail'));
const AddBook = lazy(() => import('./pages/AddBook'));
const DiscussionDetail = lazy(() => import('./pages/DiscussionDetail'));
const Friends = lazy(() => import('./pages/Friends'));
const Notifications = lazy(() => import('./pages/Notifications'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const Settings = lazy(() => import('./pages/Settings'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Terms = lazy(() => import('./pages/Terms'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));

// Wires push-notification listeners (registration + tap-to-navigate) once at
// app startup. No-op on web (isSupported() false inside initListeners).
function PushBridge() {
  const navigate = useNavigate();

  useEffect(() => {
    initListeners(navigate);
  }, [navigate]);

  return null;
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return user ? children : <Navigate to="/login" />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return user ? <Navigate to="/books" /> : children;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <PushBridge />
        <Suspense fallback={<LoadingSpinner size="large" />}>
        <Routes>
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route
            path="/forgot-password"
            element={<PublicRoute><ForgotPassword /></PublicRoute>}
          />
          {/* Not a PublicRoute: an emailed link must work even if this browser is signed in. */}
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/books"
            element={
              <PrivateRoute>
                <BookList />
              </PrivateRoute>
            }
          />
          <Route
            path="/books/new"
            element={
              <PrivateRoute>
                <AddBook />
              </PrivateRoute>
            }
          />
          <Route
            path="/books/:bookId"
            element={
              <PrivateRoute>
                <BookDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/books/:bookId/discussions/:discussionId"
            element={
              <PrivateRoute>
                <DiscussionDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/friends"
            element={
              <PrivateRoute>
                <Friends />
              </PrivateRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <PrivateRoute>
                <Notifications />
              </PrivateRoute>
            }
          />
          <Route
            path="/users/:userId"
            element={
              <PrivateRoute>
                <UserProfile />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <Settings />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/*"
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          />
          <Route path="/" element={<Navigate to="/books" />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="*" element={<Navigate to="/books" />} />
        </Routes>
        </Suspense>
      </Router>
    </AuthProvider>
  );
}

export default App;